import { useState, useEffect, useMemo } from 'react';
import { Copy, Download, Settings2, RefreshCcw, ExternalLink, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SlidePanel } from '@/shared/components';
import { useBills } from '../hooks';
import type { Bill, BillStatus } from '../types';
import { BillDetailPanel } from '../components/BillDetailPanel';
import AttachProofDialog from '../components/AttachProofDialog';

type StatusValue = Exclude<BillStatus, 'all'>;

const STATUS_OPTIONS: { value: StatusValue; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const STATUS_COLOR: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  void: 'bg-gray-100 text-gray-800',
};

type ColumnKey = 'status' | 'doc_number' | 'vendor_name' | 'txn_date' | 'due_date' | 'total_amt' | 'balance' | 'payment_date' | 'payment_proof' | 'qb_link';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  align?: 'left' | 'right';
}

const COLUMNS: ColumnDef[] = [
  { key: 'status', label: 'Status' },
  { key: 'doc_number', label: 'Bill #' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'txn_date', label: 'Bill date' },
  { key: 'due_date', label: 'Due date' },
  { key: 'total_amt', label: 'Total', align: 'right' },
  { key: 'balance', label: 'Balance', align: 'right' },
  { key: 'payment_date', label: 'Paid on' },
  { key: 'payment_proof', label: 'Proof' },
  { key: 'qb_link', label: 'QB' },
];

const STATUS_FILTER_KEY = 'atek_bills_status_filter';
const VISIBLE_COLS_KEY = 'atek_bills_visible_cols';
const SORT_KEY_KEY = 'atek_bills_sort_key';
const SORT_DIR_KEY = 'atek_bills_sort_dir';
const PANEL_STORAGE_KEY = 'atek_bills_panel_width';

type SortKey = Exclude<ColumnKey, 'qb_link' | 'status' | 'payment_proof'>;
type SortDir = 'asc' | 'desc';

const SORTABLE_KEYS: SortKey[] = ['doc_number', 'vendor_name', 'txn_date', 'due_date', 'total_amt', 'balance', 'payment_date'];

function formatDateIso(date: string | null | undefined): string {
  if (!date) return '—';
  const m = String(date).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '—';
}

function loadVisibleCols(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(VISIBLE_COLS_KEY);
    if (raw) return new Set(JSON.parse(raw) as ColumnKey[]);
  } catch { /* ignore */ }
  return new Set(COLUMNS.map((c) => c.key));
}

function copyToClipboard(rows: Bill[], visibleKeys: ColumnKey[]) {
  const headers = visibleKeys.map((k) => COLUMNS.find((c) => c.key === k)?.label ?? k).join('\t');
  const lines = rows.map((b) => visibleKeys.map((k) => formatCell(b, k)).join('\t'));
  navigator.clipboard.writeText([headers, ...lines].join('\n'));
}

function exportCsv(rows: Bill[], visibleKeys: ColumnKey[]) {
  const headers = visibleKeys.map((k) => COLUMNS.find((c) => c.key === k)?.label ?? k);
  const csvLines = [
    headers.join(','),
    ...rows.map((b) => visibleKeys.map((k) => `"${String(formatCell(b, k)).replace(/"/g, '""')}"`).join(',')),
  ];
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bills-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatCell(b: Bill, key: ColumnKey): string | number {
  switch (key) {
    case 'status': return b.status;
    case 'doc_number': return b.doc_number ?? '';
    case 'vendor_name': return b.vendor_name ?? '';
    case 'txn_date': return formatDateIso(b.txn_date);
    case 'due_date': return formatDateIso(b.due_date);
    case 'total_amt': return Number(b.total_amt);
    case 'balance': return Number(b.balance);
    case 'payment_date': return formatDateIso(b.last_payment_date);
    case 'payment_proof': return b.payment_proof_gdrive_url ?? b.payment_proof_dms_id ?? '';
    case 'qb_link': return b.quickbooks_bill_id;
    default: return '';
  }
}

export default function BillsListPage() {
  const [selectedStatuses, setSelectedStatuses] = useState<Set<StatusValue>>(() => {
    try {
      const raw = localStorage.getItem(STATUS_FILTER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as StatusValue[];
        const valid = arr.filter((s) => STATUS_OPTIONS.some((o) => o.value === s));
        return new Set(valid);
      }
    } catch { /* ignore */ }
    return new Set<StatusValue>(['open', 'overdue']);
  });
  const [search, setSearch] = useState('');
  const [visibleCols, setVisibleCols] = useState<Set<ColumnKey>>(loadVisibleCols);
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [attachBill, setAttachBill] = useState<Bill | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const saved = localStorage.getItem(SORT_KEY_KEY) as SortKey | null;
    return saved && SORTABLE_KEYS.includes(saved) ? saved : 'txn_date';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const saved = localStorage.getItem(SORT_DIR_KEY) as SortDir | null;
    return saved === 'asc' || saved === 'desc' ? saved : 'desc';
  });

  useEffect(() => {
    localStorage.setItem(STATUS_FILTER_KEY, JSON.stringify(Array.from(selectedStatuses)));
  }, [selectedStatuses]);

  const toggleStatus = (s: StatusValue) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  useEffect(() => {
    localStorage.setItem(VISIBLE_COLS_KEY, JSON.stringify(Array.from(visibleCols)));
  }, [visibleCols]);
  useEffect(() => { localStorage.setItem(SORT_KEY_KEY, sortKey); }, [sortKey]);
  useEffect(() => { localStorage.setItem(SORT_DIR_KEY, sortDir); }, [sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'txn_date' || key === 'due_date' ? 'desc' : 'asc');
    }
  };

  // Always fetch 'all' from backend; multi-select filters client-side.
  // An empty selection means "show every status" (no filter).
  const { data, isLoading, error, refetch, isFetching } = useBills({
    status: 'all',
    search,
    pageNumber: 0,
    pageLength: 100,
  });
  const rawBills = data?.data ?? [];
  const bills = useMemo(() => {
    const filtered = selectedStatuses.size === 0
      ? rawBills
      : rawBills.filter((b) => selectedStatuses.has(b.status as StatusValue));
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'total_amt': return (Number(a.total_amt) - Number(b.total_amt)) * dir;
        case 'balance': return (Number(a.balance) - Number(b.balance)) * dir;
        case 'payment_date': {
          const av = a.last_payment_date ?? '';
          const bv = b.last_payment_date ?? '';
          if (av === bv) return 0;
          if (av === '') return 1;
          if (bv === '') return -1;
          return av < bv ? -dir : dir;
        }
        case 'txn_date':
        case 'due_date':
        case 'doc_number':
        case 'vendor_name': {
          const av = (a[sortKey] ?? '') as string;
          const bv = (b[sortKey] ?? '') as string;
          if (av === bv) return 0;
          if (av === '') return 1;
          if (bv === '') return -1;
          return av < bv ? -dir : dir;
        }
        default: return 0;
      }
    });
    return sorted;
  }, [rawBills, sortKey, sortDir, selectedStatuses]);
  const visibleKeys = COLUMNS.filter((c) => visibleCols.has(c.key)).map((c) => c.key);

  const openBill = (b: Bill) => {
    setSelectedBillId(b.id);
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) setSelectedBillId(null);
  };

  const toggleColumn = (key: ColumnKey) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const totals = bills.reduce(
    (acc, b) => {
      acc.total += Number(b.total_amt);
      acc.balance += Number(b.balance);
      return acc;
    },
    { total: 0, balance: 0 },
  );

  return (
    <div className="space-y-4 p-6">
      {/* Toolbar (filter-style — column filters live in the table header) */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search bill # or vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-64"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => { copyToClipboard(bills, visibleKeys); toast.success('Copied to clipboard'); }}
          >
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { exportCsv(bills, visibleKeys); toast.success(`Exported ${bills.length} rows`); }}
          >
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleCols.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCcw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main content area (rocks-style flex with optional panel) */}
      <div className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="border rounded-lg overflow-hidden bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleKeys.map((key) => {
                    const col = COLUMNS.find((c) => c.key === key)!;
                    const sortable = (SORTABLE_KEYS as ColumnKey[]).includes(key);
                    const isActive = sortable && sortKey === key;
                    return (
                      <TableHead
                        key={key}
                        className={cn(col.align === 'right' ? 'text-right' : '', sortable && 'cursor-pointer select-none')}
                        onClick={sortable ? () => toggleSort(key as SortKey) : undefined}
                      >
                        <span className={cn('inline-flex items-center gap-1', col.align === 'right' && 'justify-end w-full')}>
                          {col.label}
                          {isActive && (sortDir === 'asc'
                            ? <ArrowUp className="h-3 w-3" />
                            : <ArrowDown className="h-3 w-3" />)}
                        </span>
                      </TableHead>
                    );
                  })}
                </TableRow>
                {/* Filter row — always visible, lives under sort headers */}
                <TableRow className="hover:bg-transparent">
                  {visibleKeys.map((key) => (
                    <TableHead key={`filter-${key}`} className="py-1">
                      {key === 'status' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-7 px-2 font-normal w-full justify-between">
                              <span className="truncate">
                                {selectedStatuses.size === 0
                                  ? 'All'
                                  : selectedStatuses.size === STATUS_OPTIONS.length
                                    ? 'All'
                                    : Array.from(selectedStatuses).map((s) => STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s).join(', ')}
                              </span>
                              <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {STATUS_OPTIONS.map((opt) => (
                              <DropdownMenuCheckboxItem
                                key={opt.value}
                                checked={selectedStatuses.has(opt.value)}
                                onCheckedChange={() => toggleStatus(opt.value)}
                                onSelect={(e) => e.preventDefault()}
                              >
                                {opt.label}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={visibleKeys.length} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {error && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={visibleKeys.length} className="text-center text-muted-foreground py-8">
                      Backend error: {(error as Error).message}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !error && bills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleKeys.length} className="text-center text-muted-foreground py-8">
                      No bills.
                    </TableCell>
                  </TableRow>
                )}
                {bills.map((b) => (
                  <TableRow
                    key={b.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => openBill(b)}
                  >
                    {visibleCols.has('status') && (
                      <TableCell><Badge className={STATUS_COLOR[b.status] ?? ''}>{b.status}</Badge></TableCell>
                    )}
                    {visibleCols.has('doc_number') && (
                      <TableCell className="font-mono text-sm">{b.doc_number ?? '—'}</TableCell>
                    )}
                    {visibleCols.has('vendor_name') && (
                      <TableCell>{b.vendor_name ?? '—'}</TableCell>
                    )}
                    {visibleCols.has('txn_date') && (
                      <TableCell className="font-mono text-sm">{formatDateIso(b.txn_date)}</TableCell>
                    )}
                    {visibleCols.has('due_date') && (
                      <TableCell className="font-mono text-sm">{formatDateIso(b.due_date)}</TableCell>
                    )}
                    {visibleCols.has('total_amt') && (
                      <TableCell className="text-right tabular-nums">{formatCurrency(Number(b.total_amt))}</TableCell>
                    )}
                    {visibleCols.has('balance') && (
                      <TableCell className="text-right tabular-nums">{formatCurrency(Number(b.balance))}</TableCell>
                    )}
                    {visibleCols.has('payment_date') && (
                      <TableCell
                        className="font-mono text-sm"
                        title={b.last_payment_method && b.last_payment_account_name ? `${b.last_payment_method} — ${b.last_payment_account_name}` : (b.last_payment_method ?? '')}
                      >
                        {formatDateIso(b.last_payment_date)}
                      </TableCell>
                    )}
                    {visibleCols.has('payment_proof') && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {b.payment_proof_gdrive_url ? (
                            <a
                              href={b.payment_proof_gdrive_url}
                              target="_blank"
                              rel="noreferrer"
                              title="Open payment proof in Drive"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : b.payment_proof_dms_id ? (
                            <a
                              href={`/acc/dms/${b.payment_proof_dms_id}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Open payment proof in DMS"
                            >
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            </a>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => { setAttachBill(b); setAttachOpen(true); }}
                            className="text-xs underline decoration-dotted text-muted-foreground hover:text-foreground"
                            title={b.payment_proof_dms_id || b.payment_proof_gdrive_url ? 'Replace proof' : 'Attach proof'}
                          >
                            {b.payment_proof_dms_id || b.payment_proof_gdrive_url ? 'edit' : 'attach'}
                          </button>
                        </div>
                      </TableCell>
                    )}
                    {visibleCols.has('qb_link') && (
                      <TableCell>
                        <a
                          href={`https://qbo.intuit.com/app/bill?txnId=${b.quickbooks_bill_id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Open in QuickBooks"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {bills.length > 0 && (
                  <TableRow className="font-bold bg-muted/50">
                    <TableCell colSpan={Math.max(1, visibleKeys.indexOf('total_amt'))}>
                      Total ({bills.length})
                    </TableCell>
                    {visibleCols.has('total_amt') && (
                      <TableCell className="text-right tabular-nums">{formatCurrency(totals.total)}</TableCell>
                    )}
                    {visibleCols.has('balance') && (
                      <TableCell className="text-right tabular-nums">{formatCurrency(totals.balance)}</TableCell>
                    )}
                    {visibleCols.has('qb_link') && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <SlidePanel open={panelOpen} storageKey={PANEL_STORAGE_KEY}>
          <BillDetailPanel billId={selectedBillId} onClose={() => handlePanelClose(false)} />
        </SlidePanel>
      </div>

      <AttachProofDialog
        bill={attachBill}
        open={attachOpen}
        onOpenChange={(o) => { setAttachOpen(o); if (!o) setAttachBill(null); }}
      />
    </div>
  );
}
